import { useState } from "react";

interface Community {
  name: string;
  route: string;
}

interface Subregion {
  name: string;
  description: string;
  communities: Community[];
  hub: { name: string; route: string };
}

interface Region {
  id: string;
  name: string;
  icon: string;
  subregions: Subregion[];
}

const regions: Region[] = [
  {
    id: "phoenix-metro",
    name: "Phoenix Metro",
    icon: "ri-sun-line",
    subregions: [
      {
        name: "East Valley",
        description: "Mesa, Gilbert, Chandler, and Tempe form the East Valley core. The Superstition Freeway (US-60) connects to Phoenix Sky Harbor in 20–30 minutes. Queen Creek and San Tan Valley are rapidly expanding.",
        hub: { name: "Phoenix", route: "/cities/phoenix" },
        communities: [
          { name: "Mesa", route: "/cities/mesa" }, { name: "Gilbert", route: "/cities/gilbert" },
          { name: "Chandler", route: "/cities/chandler" }, { name: "Tempe", route: "/cities/tempe" },
          { name: "Apache Junction", route: "/cities/apache-junction" }, { name: "Queen Creek", route: "#" },
          { name: "San Tan Valley", route: "#" }, { name: "Fountain Hills", route: "#" },
          { name: "Gold Canyon", route: "#" }, { name: "Casa Grande", route: "/cities/casa-grande" },
        ],
      },
      {
        name: "North Valley & Scottsdale",
        description: "Scottsdale, Paradise Valley, and North Phoenix neighborhoods sit north of the Salt River. The 101 Loop connects to Sky Harbor Airport in 30 minutes. Carefree and Cave Creek are mountain communities with higher elevation.",
        hub: { name: "Scottsdale", route: "/cities/scottsdale" },
        communities: [
          { name: "Scottsdale", route: "#" }, { name: "Paradise Valley", route: "/cities/paradise-valley" },
          { name: "Carefree", route: "#" }, { name: "Cave Creek", route: "#" },
          { name: "Deer Valley", route: "#" }, { name: "North Mountain", route: "#" },
          { name: "Desert Ridge", route: "#" }, { name: "Fountain Hills", route: "#" },
          { name: "Rio Verde", route: "#" }, { name: "New River", route: "#" },
        ],
      },
      {
        name: "West Valley",
        description: "Glendale, Peoria, Surprise, Avondale, Goodyear, and Buckeye — the fastest-growing corridor in the metro. State Farm Stadium is the anchor. The 303 Loop connects to I-10 for Phoenix access.",
        hub: { name: "Glendale", route: "/cities/glendale-az" },
        communities: [
          { name: "Peoria", route: "/cities/peoria-az" }, { name: "Surprise", route: "/cities/surprise" },
          { name: "Avondale", route: "/cities/avondale" }, { name: "Goodyear", route: "/cities/goodyear" },
          { name: "Buckeye", route: "/cities/buckeye" }, { name: "El Mirage", route: "#" },
          { name: "Litchfield Park", route: "#" }, { name: "Waddell", route: "#" },
          { name: "Sun City", route: "#" }, { name: "Sun City West", route: "#" },
        ],
      },
      {
        name: "South Valley & Ahwatukee",
        description: "South Phoenix, Ahwatukee, and Laveen are connected via I-10 to the airport in 15–20 minutes. South Mountain Park forms the southern boundary. The Gila River Indian Community is nearby.",
        hub: { name: "Phoenix", route: "/cities/phoenix" },
        communities: [
          { name: "Ahwatukee", route: "#" }, { name: "Laveen", route: "#" },
          { name: "South Phoenix", route: "#" }, { name: "Laveen Village", route: "#" },
          { name: "Gila River Indian Community", route: "#" }, { name: "Guadalupe", route: "#" },
          { name: "Maricopa", route: "/cities/maricopa" }, { name: "Ak-Chin", route: "#" },
        ],
      },
    ],
  },
  {
    id: "tucson-south",
    name: "Southern Arizona",
    icon: "ri-sun-line",
    subregions: [
      {
        name: "Tucson Metro",
        description: "Tucson International Airport (TUS) is 10 miles south of Downtown. Oro Valley and Marana are north of the Catalina Mountains; Green Valley and Sahuarita are south on I-19. The University of Arizona drives substantial student travel.",
        hub: { name: "Tucson", route: "/cities/tucson" },
        communities: [
          { name: "Oro Valley", route: "/cities/oro-valley" }, { name: "Marana", route: "/cities/marana" },
          { name: "Sahuarita", route: "/cities/sahuarita" }, { name: "Green Valley", route: "/cities/green-valley" },
          { name: "Vail", route: "#" }, { name: "Rita Ranch", route: "#" },
          { name: "Catalina", route: "#" }, { name: "Saddlebrooke", route: "#" },
          { name: "Tortolita", route: "#" }, { name: "Drexel Heights", route: "#" },
        ],
      },
      {
        name: "US-Mexico Border Corridor",
        description: "Nogales, Douglas, and San Luis are major border crossings. Santa Cruz County (Nogales) and Cochise County (Douglas) serve significant cross-border populations. Sierra Vista is near Fort Huachuca.",
        hub: { name: "Nogales", route: "/cities/nogales" },
        communities: [
          { name: "Rio Rico", route: "#" }, { name: "Tubac", route: "#" },
          { name: "Patagonia", route: "#" }, { name: "Sierra Vista", route: "#" },
          { name: "Huachuca City", route: "#" }, { name: "Bisbee", route: "#" },
          { name: "Douglas", route: "#" }, { name: "San Luis", route: "/cities/san-luis" },
          { name: "Somerton", route: "/cities/somerton" }, { name: "Gadsden", route: "#" },
        ],
      },
    ],
  },
  {
    id: "northern-mountains",
    name: "Northern Arizona",
    icon: "ri-landscape-line",
    subregions: [
      {
        name: "Flagstaff & Coconino Plateau",
        description: "Flagstaff sits at 6,900 feet elevation on I-40/I-17 intersection. It's the gateway to Grand Canyon, Sedona, and the Navajo Nation. Northern Arizona University (NAU) adds to the population. US-180 connects to the Grand Canyon.",
        hub: { name: "Flagstaff", route: "/cities/flagstaff" },
        communities: [
          { name: "Sedona", route: "/cities/sedona" }, { name: "Williams", route: "#" },
          { name: "Bellemont", route: "#" }, { name: "Munds Park", route: "#" },
          { name: "Kachina Village", route: "#" }, { name: "Oak Creek Canyon", route: "#" },
          { name: "Village of Oak Creek", route: "#" }, { name: "Cornville", route: "#" },
          { name: "Rimrock", route: "#" }, { name: "Camp Verde", route: "#" },
        ],
      },
      {
        name: "Prescott & Yavapai County",
        description: "Prescott, the 'Mile High City,' sits at 5,300 feet. Prescott Valley, Chino Valley, and Dewey-Humboldt are the 'Quad Cities' area. Prescott is a popular retirement destination — seniors are frequent passport users.",
        hub: { name: "Prescott", route: "/cities/prescott" },
        communities: [
          { name: "Prescott Valley", route: "/cities/prescott-valley" }, { name: "Chino Valley", route: "#" },
          { name: "Dewey-Humboldt", route: "#" }, { name: "Cottonwood", route: "/cities/cottonwood" },
          { name: "Clarkdale", route: "#" }, { name: "Jerome", route: "#" },
          { name: "Mayer", route: "#" }, { name: "Black Canyon City", route: "#" },
          { name: "Spring Valley", route: "#" }, { name: "Congress", route: "#" },
        ],
      },
      {
        name: "White Mountains & Rim Country",
        description: "Show Low, Pinetop-Lakeside, and Payson are the 'Rim Country' — Arizona's pine forest communities. The Mogollon Rim creates a dramatic elevation change. US-260 and US-87 connect to the Phoenix metro.",
        hub: { name: "Show Low", route: "/cities/show-low" },
        communities: [
          { name: "Pinetop-Lakeside", route: "#" }, { name: "Payson", route: "#" },
          { name: "Pine", route: "#" }, { name: "Strawberry", route: "#" },
          { name: "Star Valley", route: "#" }, { name: "Snowflake", route: "#" },
          { name: "Taylor", route: "#" }, { name: "Heber-Overgaard", route: "#" },
          { name: "Holbrook", route: "/cities/holbrook" }, { name: "Winslow", route: "/cities/winslow" },
        ],
      },
    ],
  },
  {
    id: "western-rivers",
    name: "Western & Colorado River",
    icon: "ri-map-2-line",
    subregions: [
      {
        name: "Yuma County",
        description: "Yuma is the warmest city in Arizona, directly on the California border. The Yuma Proving Ground and Marine Corps Air Station Yuma create a large military population needing frequent passport updates.",
        hub: { name: "Yuma", route: "/cities/yuma" },
        communities: [
          { name: "Fortuna Foothills", route: "#" }, { name: "San Luis", route: "/cities/san-luis" },
          { name: "Somerton", route: "/cities/somerton" }, { name: "Gadsden", route: "#" },
          { name: "Cocopah", route: "#" }, { name: "Wellton", route: "#" },
          { name: "Tacna", route: "#" }, { name: "Dateland", route: "#" },
        ],
      },
      {
        name: "Mohave County & Colorado River",
        description: "Kingman, Lake Havasu City, and Bullhead City sit along the Colorado River. The London Bridge in Lake Havasu City is a major tourist attraction. Laughlin, Nevada is directly across the river from Bullhead City.",
        hub: { name: "Kingman", route: "/cities/kingman" },
        communities: [
          { name: "Lake Havasu City", route: "/cities/lake-havasu-city" }, { name: "Bullhead City", route: "/cities/bullhead-city" },
          { name: "Fort Mohave", route: "#" }, { name: "Mohave Valley", route: "#" },
          { name: "Laughlin NV area", route: "#" }, { name: "Golden Valley", route: "#" },
          { name: "Peach Springs", route: "#" }, { name: "Dolan Springs", route: "#" },
          { name: "Parker", route: "#" }, { name: "Topock", route: "#" },
        ],
      },
    ],
  },
  {
    id: "east-southeast",
    name: "East & Southeast Arizona",
    icon: "ri-compass-line",
    subregions: [
      {
        name: "Southeast Arizona",
        description: "Sierra Vista, Bisbee, and Tombstone are the Old West and high-desert corridor. The Bisbee area is an arts destination. Cochise County is large and sparsely populated — courier pickup is a key service.",
        hub: { name: "Sierra Vista", route: "/cities/sierra-vista" },
        communities: [
          { name: "Bisbee", route: "#" }, { name: "Tombstone", route: "#" },
          { name: "Huachuca City", route: "#" }, { name: "Benson", route: "#" },
          { name: "Willcox", route: "#" }, { name: "Safford", route: "#" },
          { name: "Thatcher", route: "#" }, { name: "Clifton", route: "#" },
          { name: "Morenci", route: "#" }, { name: "Pirtleville", route: "#" },
        ],
      },
      {
        name: "Petrified Forest & Navajo Country",
        description: "Holbrook, Winslow, and Page are the northern corridor. The Petrified Forest and Painted Desert are major attractions. Page is the gateway to Lake Powell, Horseshoe Bend, and the Glen Canyon Dam.",
        hub: { name: "Page", route: "/cities/page-az" },
        communities: [
          { name: "LeChee", route: "#" }, { name: "Kaibito", route: "#" },
          { name: "Bitter Springs", route: "#" }, { name: "Tuba City", route: "#" },
          { name: "Kayenta", route: "#" }, { name: "Chinle", route: "#" },
          { name: "Window Rock", route: "#" }, { name: "Ganado", route: "#" },
          { name: "Holbrook", route: "/cities/holbrook" }, { name: "Joseph City", route: "#" },
        ],
      },
    ],
  },
];

export default function ArizonaCommunitiesWeServe() {
  const [activeRegion, setActiveRegion] = useState(regions[0].id);

  const current = regions.find((r) => r.id === activeRegion) || regions[0];

  return (
    <section id="communities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Statewide Coverage
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Communities We Serve Across Arizona
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Don't see your city in the directory above? You're still covered. Every Arizona community below is served through our nearest hub — with the same fast, reliable expedited passport processing.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => setActiveRegion(region.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeRegion === region.id
                  ? "bg-[#1A365D] text-white"
                  : "bg-[#EEF2F8] text-[#1A365D] hover:bg-[#dce5f3]"
              }`}
            >
              <i className={`${region.icon} text-base`}></i>
              {region.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {current.subregions.map((sub, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="font-bold text-[#1A365D] text-base">{sub.name}</h3>
                {sub.hub.route !== "#" ? (
                  <a
                    href={sub.hub.route}
                    className="flex-shrink-0 inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap"
                  >
                    Hub: {sub.hub.name} <i className="ri-arrow-right-line"></i>
                  </a>
                ) : (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                    Hub: {sub.hub.name}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{sub.description}</p>
              <div className="flex flex-wrap gap-2">
                {sub.communities.map((c, j) => (
                  c.route !== "#" ? (
                    <a
                      key={j}
                      href={c.route}
                      className="inline-flex items-center text-xs font-medium text-[#1A365D] bg-white border border-gray-200 rounded-full px-3 py-1 hover:border-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap cursor-pointer"
                    >
                      {c.name}
                    </a>
                  ) : (
                    <span
                      key={j}
                      className="inline-flex items-center text-xs font-medium text-[#6B7280] bg-white border border-gray-200 rounded-full px-3 py-1 whitespace-nowrap"
                    >
                      {c.name}
                    </span>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-sm mt-8">
          Not finding your exact community? <a href="tel:8772530084" className="text-[#1A365D] font-semibold hover:text-[#F6C344] transition-colors">Call us at 877-253-0084</a> — we serve all of Arizona.
        </p>
      </div>
    </section>
  );
}